import React, { Component, Fragment } from "react";
import { Link } from "react-router-dom";
import CoinGeckoAdapter from "../../utils/Adapters/CoinGeckoAdapter";
import Formatter from "../../utils/Formatter";
import ContentArea from "../ContentArea/ContentArea";
import StatusMessage from "../StatusMessage/StatusMessage";
import "./Style.css";

const INITIAL_CURRENCY_COUNT = 30;
const CURRENCY_INTERVAL_COUNT = 30;
const CURRENCY_MAX_COUNT = 90;

type CurrencyInfo = {
  id: string;
  symbol: string;
  name: string;
  image: string;
  current_price: string;
  market_cap: string;
  ath_date: string;
  ath: string;
  ath_change_percentage: string;
};

type CurrencyStats = {
  id: string;
  currentBtcPrice: number;
  currentBtcMarketCap: number;
  btcAth: number;
  btcAthDate?: Date;
  btcAthDrop?: number;
};

type Props = {};
type SortKey = "position" | "marketCap" | "price" | "down" | "when";

type State = {
  isLoading: boolean;
  currenciesStats: CurrencyStats[];
  sortKey?: SortKey;
  sortDirection: "asc" | "desc";
};

class CurrencyCardArea extends Component<Props, State> {
  private topCurrenciesInfo?: CurrencyInfo[];

  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: true,
      currenciesStats: [],
      sortKey: undefined,
      sortDirection: "asc",
    };
  }

  componentDidMount() {
    console.log("-------componentDidMount------------");
    this.initialize(); // TODO: add repeat if not loaded
  }

  private async initialize() {
    this.setState({ isLoading: true });

    this.topCurrenciesInfo = (await CoinGeckoAdapter.getTopCurrenciesByMarketCap(
      CURRENCY_MAX_COUNT + 1,
    )) as CurrencyInfo[];
    if (!this.topCurrenciesInfo) {
      console.error("Unable to retrieve top currencies info.");
      this.openDialog();
      this.setState({ isLoading: false });
      return;
    }

    this.setCurrenciesStats(INITIAL_CURRENCY_COUNT);
    this.setState({ isLoading: false });
  }

  private loadMoreCurrencyStats = async () => {
    const currentStatsCount = this.state.currenciesStats.length;
    if (currentStatsCount >= CURRENCY_MAX_COUNT) {
      console.warn("Unable to load more currency stats, limit exceeded");
      return;
    }

    if (!this.topCurrenciesInfo) {
      console.error("Currencies info was not retrieved yet");
      return;
    }

    const nextStatsCount = currentStatsCount + CURRENCY_INTERVAL_COUNT;
    this.setCurrenciesStats(nextStatsCount);
  };

  private setCurrenciesStats(count: number) {
    const currenciesStats = [];
    this.topCurrenciesInfo.slice(1, count + 1).forEach((currencyInfo) => {
      const {
        id,
        current_price,
        market_cap,
        ath_date,
        ath,
        ath_change_percentage,
      } = currencyInfo;

      currenciesStats.push({
        id: id,
        currentBtcPrice: parseFloat(current_price),
        currentBtcMarketCap: parseFloat(market_cap),
        btcAthDate: new Date(ath_date),
        btcAth: parseFloat(ath),
        btcAthDrop: parseFloat(ath_change_percentage),
      });
    });

    this.setState({ currenciesStats });
  }

  private openDialog() {
    const modal = document.querySelector("#modalDialog") as any; // TODO: remove any

    if (modal) {
      modal.showModal();
    } else {
      console.error("Fail to load modal dialog");
    }
  }

  private toggleSort = (sortKey: SortKey) => {
    this.setState((prev) => {
      if (prev.sortKey !== sortKey) {
        return { sortKey, sortDirection: "asc" };
      }

      return {
        sortKey,
        sortDirection: prev.sortDirection === "asc" ? "desc" : "asc",
      };
    });
  };

  private getSortIndicator(sortKey: SortKey) {
    if (this.state.sortKey !== sortKey) {
      return "";
    }

    return this.state.sortDirection === "asc" ? "^" : "v";
  }

  private compareRows(
    a: {
      position: number;
      currentBtcPrice?: number;
      currentBtcMarketCap?: number;
      btcAthDrop?: number;
      btcAthDate?: Date;
    },
    b: {
      position: number;
      currentBtcPrice?: number;
      currentBtcMarketCap?: number;
      btcAthDrop?: number;
      btcAthDate?: Date;
    },
  ) {
    const { sortKey, sortDirection } = this.state;
    if (!sortKey) {
      return 0;
    }

    const order = sortDirection === "asc" ? 1 : -1;

    if (sortKey === "position") {
      return (a.position - b.position) * order;
    }

    if (sortKey === "down") {
      const aDrop = Math.abs(a.btcAthDrop ?? 0);
      const bDrop = Math.abs(b.btcAthDrop ?? 0);
      return (aDrop - bDrop) * order;
    }

    if (sortKey === "marketCap") {
      return ((a.currentBtcMarketCap ?? 0) - (b.currentBtcMarketCap ?? 0)) * order;
    }

    if (sortKey === "price") {
      return ((a.currentBtcPrice ?? 0) - (b.currentBtcPrice ?? 0)) * order;
    }

    const aWhen = a.btcAthDate ? a.btcAthDate.getTime() : 0;
    const bWhen = b.btcAthDate ? b.btcAthDate.getTime() : 0;
    return (aWhen - bWhen) * order;
  }

  private renderCurrencyList() {
    if (!this.topCurrenciesInfo) {
      console.error("Unable to render currencies");
      return <Fragment></Fragment>;
    }

    const numberOfLoadedCurrencies = this.state.currenciesStats.length;
    const currenciesInfoToRender = this.topCurrenciesInfo.slice(
      1,
      numberOfLoadedCurrencies + 1,
    );
    const now = new Date();
    const statsById = new Map(
      this.state.currenciesStats.map((stats) => [stats.id, stats]),
    );

    const rows = currenciesInfoToRender.map((currencyInfo, index) => {
        const stats = statsById.get(currencyInfo.id);

        return {
          id: currencyInfo.id,
          position: index + 1,
          image: currencyInfo.image,
          symbol: currencyInfo.symbol.toUpperCase(),
          name: currencyInfo.name,
          currentBtcPrice: stats?.currentBtcPrice,
          currentBtcMarketCap: stats?.currentBtcMarketCap,
          btcAthDrop: stats?.btcAthDrop,
          btcAthDate: stats?.btcAthDate,
        };
      });

    if (this.state.sortKey) {
      rows.sort((a, b) => this.compareRows(a, b));
    }

    const formatPrice = (btcPrice?: number) => {
      if (btcPrice === undefined) {
        return "-";
      }

      return Formatter.getBtcPrice(btcPrice) ?? "<0.1 SAT";
    };

    return (
      <div className="currency-list">
        <div className="currency-list-header">
          <button
            type="button"
            className="currency-sort-button currency-col-order"
            onClick={() => this.toggleSort("position")}
          >
            # {this.getSortIndicator("position")}
          </button>

          <div className="currency-col-crypto">Cryptocurrency</div>
          <button
            type="button"
            className="currency-sort-button currency-col-marketcap"
            onClick={() => this.toggleSort("marketCap")}
          >
            Market Cap (BTC) {this.getSortIndicator("marketCap")}
          </button>

          <button
            type="button"
            className="currency-sort-button currency-col-price"
            onClick={() => this.toggleSort("price")}
          >
            Price {this.getSortIndicator("price")}
          </button>

          <button
            type="button"
            className="currency-sort-button currency-col-when"
            onClick={() => this.toggleSort("when")}
          >
            BTC ATH Reached {this.getSortIndicator("when")}
          </button>

          <button
            type="button"
            className="currency-sort-button currency-col-down"
            onClick={() => this.toggleSort("down")}
          >
            Below its BTC ATH {this.getSortIndicator("down")}
          </button>
        </div>

        <div className="currency-list-body">
          {rows.map((row) => (
            <Link key={row.id} to={`/detail/${row.id}`} className="currency-list-row">
              <div className="currency-col-order">{row.position}</div>

              <div className="currency-col-crypto">
                <div className="currency-crypto-main">
                  <img className="currency-icon" src={row.image} alt={`${row.name} logo`} />
                  <div className="currency-crypto-text">
                    <div className="currency-symbol">{row.symbol}</div>
                    <div className="currency-name">{row.name}</div>
                  </div>
                </div>
              </div>

              <div className="currency-col-marketcap">
                {row.currentBtcMarketCap
                  ? Formatter.getBtcPrice(row.currentBtcMarketCap)
                  : "-"}
              </div>

              <div className="currency-col-price">
                {formatPrice(row.currentBtcPrice)}
              </div>

              <div className="currency-col-when">
                {row.btcAthDate
                  ? `${Formatter.getTime(now.getTime() - row.btcAthDate.getTime())} ago`
                  : "-"}
              </div>

              <div className="currency-col-down">
                {row.btcAthDrop !== undefined
                  ? `${Math.abs(row.btcAthDrop).toFixed(1)} %`
                  : "-"}
              </div>

              <div className="currency-mobile-meta">
                <span>
                  Market Cap:{" "}
                  <b>
                    {row.currentBtcMarketCap
                      ? Formatter.getBtcPrice(row.currentBtcMarketCap)
                      : "-"}
                  </b>
                </span>
                <span>
                  Price:{" "}
                  <b>{formatPrice(row.currentBtcPrice)}</b>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  private renderLoading() {
    return (
      <div className="progress-circle">
        <progress className="pure-material-progress-circular" />
      </div>
    );
  }

  private renderLoadingFailed() {
    return (
      <StatusMessage
        title="We could not retrieve data."
        description="Please try again in a minute."
      />
    );
  }

  private renderContent() {
    const { currenciesStats, isLoading } = this.state;

    if (isLoading) {
      if (currenciesStats?.length < INITIAL_CURRENCY_COUNT) {
        return this.renderLoading();
      }

      return (
        <Fragment>
          {this.renderCurrencyList()}
          {/* TODO: render loading or skeletons */}
        </Fragment>
      );
    }

    if (currenciesStats?.length < INITIAL_CURRENCY_COUNT) {
      return this.renderLoadingFailed();
    }

    return this.renderCurrencyList();
  }

  render() {
    const { currenciesStats, isLoading } = this.state;
    const currenciesStatsCount = currenciesStats.length;

    console.log("RENDER: Currencies stats count: " + currenciesStatsCount);
    console.log("RENDER: isLoading: " + isLoading);

    const showBottomActions = currenciesStatsCount < CURRENCY_MAX_COUNT;
    const label =
      this.state.currenciesStats.length < INITIAL_CURRENCY_COUNT
        ? "Reload"
        : "Show more";

    const bottomAction = showBottomActions
      ? {
          // TODO: showBottomActions
          label: label, // TODO: should be reload as well if fails
          disabled: isLoading,
          handleClick: async () => {
            await this.loadMoreCurrencyStats();
          },
        }
      : undefined;

    return (
      // TODO: prevent re-rendering of content https://alexsidorenko.com/blog/react-list-rerender/
      <ContentArea
        label="Cryptocurrencies in Bitcoin terms"
        labelClassName="currency-area-label"
        bottomAction={bottomAction}
      >
        {this.renderContent()}
        <div className="powered-by">
          Powered by{" "}
          <a href="https://www.coingecko.com/" target="_blank" rel="noreferrer">
            CoinGecko
          </a>
        </div>

        {/* TODO: add modal dialog https://blog.logrocket.com/creating-reusable-pop-up-modal-react/ */}
      </ContentArea>
    );
  }
}

export default CurrencyCardArea;
